// The ops calendar (B1–B8): every therapist's and rider's whole day at a
// glance, and every booking or shift one click away without leaving it.
// "A's timeline inside the Live Map shell" (Leon, 2026-10-22): full screen,
// filters in the page header, the left panel with tiles and tabs.
//
// This file owns state and derives everything once; the toolbar, the panel,
// the timeline and the week only draw what it hands them, so the rows, the
// lists, the counts and the strip can never disagree.
//
// It draws a `CalendarSource` and calls `CalendarActions` — it does not know
// whether the source is the demo's (calendar.demo.ts) or the real reads
// (calendar.source.ts). Real data has no actions yet: Assign opens Dispatch and
// Edit shift opens Schedules, the pages that save them today. See CalendarRoute.

import { Badge } from "@bednbody/ui/components/badge";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { drillInState } from "@/lib/drillIn";
import { CalendarControls } from "./CalendarControls";
import { CalendarPanel, type CalendarPanelTab, type CalendarTile } from "./CalendarPanel";
import { AssignDetail } from "./CalendarAssignDetail";
import { BookingDetail, HourDetailView, PersonDetail, TripDetail } from "./CalendarPanelDetails";
import { CalendarTimeline } from "./CalendarTimeline";
import { CalendarToolbar, type CalendarView } from "./CalendarToolbar";
import { CalendarWeek } from "./CalendarWeek";
import { buildCalendarWeek, weekDaysOf } from "./calendar-board.week";
import {
  type BoardBlock,
  buildCalendarDay,
  clockLabel,
  type CalendarActions,
  type CalendarFilters,
  type CalendarSource,
  inHub,
  isToday,
  type LegBlock,
  type MinuteSpan,
  NO_FILTERS,
  scopeCalendarDay,
  serviceLabel,
  spanLabel,
} from "./calendar-board";
import { assignOptions } from "./calendar-board.assign";
import { buildCalendarFixes, type CalendarFixItem, calendarTiles } from "./calendar-board.fix";
import { hourDetail, hourStrip } from "./calendar-board.hours";
import { boardDateTitle } from "./calendar-board.labels";
import { addCalendarDays } from "./calendar-model";

/** What the panel is showing instead of its tabs. */
export type CalendarSelection =
  | { kind: "booking"; bookingId: string }
  | { kind: "assign"; bookingId: string; pickedId: string | null }
  /** The leg's block id (`leg:…`). */
  | { kind: "trip"; legId: string }
  | { kind: "person"; staffId: string; editing: boolean }
  | { kind: "hour"; startMin: number };

interface CalendarBoardPageProps {
  source: CalendarSource;
  nowMs: number;
  today: string;
  /** `null` = read-only: Assign and Edit shift hand over to Dispatch and Schedules. */
  actions: CalendarActions | null;
  /** Tells the owner which day is shown, so it can read that week. */
  onDayChange?: (day: string) => void;
  /** Shows the "Simulated data" badge. */
  simulated: boolean;
}

export function CalendarBoardPage({
  source,
  nowMs,
  today,
  actions,
  onDayChange,
  simulated,
}: CalendarBoardPageProps) {
  const navigate = useNavigate();
  const [day, setDay] = useState(today);
  const [view, setView] = useState<CalendarView>("day");
  const [filters, setFilters] = useState<CalendarFilters>(NO_FILTERS);
  const [tab, setTab] = useState<CalendarPanelTab>("fix");
  const [selection, setSelection] = useState<CalendarSelection | null>(null);
  const [zoom, setZoom] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const week = useMemo(() => weekDaysOf(day), [day]);
  const board = useMemo(() => buildCalendarDay(source, day, nowMs), [source, day, nowMs]);
  const scoped = useMemo(
    () => scopeCalendarDay(board, filters, source.hubs),
    [board, filters, source.hubs],
  );
  const weekModel = useMemo(
    () => (view === "week" ? buildCalendarWeek(source, week, nowMs, filters) : null),
    [view, source, week, nowMs, filters],
  );
  const strip = useMemo(() => hourStrip(board, filters, scoped.range), [board, filters, scoped]);

  const hubName = useMemo(() => {
    const names = new Map(source.hubs.map((hub) => [hub.id, hub.name]));
    return (hubId: string) => names.get(hubId) ?? "Hub";
  }, [source.hubs]);
  const staffName = useMemo(() => {
    const names = new Map(source.names.map((entry) => [entry.id, entry.name]));
    return (staffId: string | null) =>
      staffId ? (names.get(staffId) ?? "Staff member") : "Nobody";
  }, [source.names]);

  const fixes = buildCalendarFixes(board, filters, hubName, strip);
  const tiles = calendarTiles(board, filters);
  const query = filters.search.trim().toLowerCase();
  const panelSessions = board.sessions.filter(
    (session) =>
      inHub(filters, session.hubId) &&
      (query === "" ||
        [session.ref, session.serviceName, staffName(session.therapistId)].some((value) =>
          value.toLowerCase().includes(query),
        )),
  );

  // ── navigation ────────────────────────────────────────────────────────────
  function goTo(next: string) {
    setDay(next);
    onDayChange?.(next);
    setSelection(null);
  }
  function step(direction: 1 | -1) {
    goTo(addCalendarDays(day, direction * (view === "day" ? 1 : 7)));
  }
  function changeFilters(next: CalendarFilters) {
    setFilters(next);
    setSelection(null);
  }

  function openFix(item: CalendarFixItem) {
    const target = item.target;
    if (target.kind === "assign") {
      setSelection({ kind: "assign", bookingId: target.bookingId, pickedId: null });
    } else if (target.kind === "person") {
      setSelection({ kind: "person", staffId: target.staffId, editing: false });
    } else setSelection({ kind: "hour", startMin: target.startMin });
  }

  function openTile(tile: CalendarTile) {
    setSelection(null);
    if (tile === "needTherapist" || tile === "notClockedIn") setTab("fix");
    else if (tile === "sessionsNow") setTab("bookings");
    else {
      const soon = board.nowMin + 30;
      setSelection({ kind: "hour", startMin: Math.floor(soon / 60) * 60 });
    }
  }

  function selectBlock(block: BoardBlock) {
    if (block.kind !== "session") setSelection({ kind: "trip", legId: block.id });
    else if (block.phase === "unassigned") {
      setSelection({ kind: "assign", bookingId: block.bookingId, pickedId: null });
    } else setSelection({ kind: "booking", bookingId: block.bookingId });
  }

  // Esc leaves a detail, like Back — unless the operator is typing in a field.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const target = event.target as HTMLElement | null;
      if (target?.closest("input, select, textarea, [role='dialog']")) return;
      setSelection(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ── fullscreen ────────────────────────────────────────────────────────────
  // The whole DOCUMENT, like the live map: menus and toasts portal to <body>,
  // and an element in fullscreen hides everything outside it.
  useEffect(() => {
    const onChange = () => setIsFullscreen(document.fullscreenElement !== null);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  async function toggleFullscreen() {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    } catch (error) {
      console.warn("[calendar] fullscreen request failed", error);
    }
  }

  // ── leaving the calendar ──────────────────────────────────────────────────
  // "← Back to Calendar" in the header brings the operator straight back.
  const here = () => drillInState(`/calendar${window.location.search}`);
  function openBooking(bookingId: string) {
    // A simulated booking has no record behind it, so its page would be empty.
    if (simulated) {
      toast.info("Simulated booking", { description: "Demo data has no real record to open." });
      return;
    }
    navigate(`/bookings?booking=${encodeURIComponent(bookingId)}`, { state: here() });
  }
  function showOnMap(staffId: string | null) {
    navigate(
      simulated ? "/live-map?demo=1" : staffId ? `/live-map?staff=${staffId}` : "/live-map",
      { state: here() },
    );
  }

  // ── changes (the demo's are in memory only; see CalendarRoute) ────────────
  function offerUndo(message: string, undo: (() => void) | null) {
    toast.success(message, undo ? { action: { label: "Undo", onClick: undo } } : undefined);
  }
  function assign(bookingId: string, staffId: string) {
    if (!actions) return;
    const session = board.sessions.find((entry) => entry.bookingId === bookingId);
    const undo = actions.assign(bookingId, staffId);
    setSelection(null);
    if (session) {
      offerUndo(
        `${staffName(staffId)} assigned to ${serviceLabel(session)} at ${clockLabel(session.startMin)}`,
        undo,
      );
    }
  }
  function saveShift(staffId: string, shift: MinuteSpan | null) {
    if (!actions) return;
    const undo = actions.saveShift(staffId, day, shift);
    setSelection({ kind: "person", staffId, editing: false });
    offerUndo(
      shift ? `${staffName(staffId)}: shift ${spanLabel(shift)}` : `${staffName(staffId)}: day off`,
      undo,
    );
  }

  // ── the panel's detail view ───────────────────────────────────────────────
  const legsOf = (bookingId: string): LegBlock[] =>
    board.rows
      .flatMap((row) => row.blocks)
      .filter(
        (block): block is LegBlock => block.kind !== "session" && block.bookingId === bookingId,
      )
      .sort((a, b) => a.startMin - b.startMin);
  const openLeg = (leg: LegBlock) => setSelection({ kind: "trip", legId: leg.id });
  const back = () => setSelection(null);

  function renderDetail(): ReactNode {
    if (!selection) return null;
    if (selection.kind === "assign" || selection.kind === "booking") {
      const options = assignOptions(board, selection.bookingId);
      if (!options) return null;
      if (selection.kind === "assign" && options.session.phase === "unassigned") {
        return (
          <AssignDetail
            hubName={hubName}
            onAssign={(staffId) => assign(selection.bookingId, staffId)}
            onBack={back}
            onOpenDispatch={actions ? undefined : () => navigate("/dispatch", { state: here() })}
            onPick={(staffId) => setSelection({ ...selection, pickedId: staffId })}
            options={options}
            pickedId={selection.pickedId}
          />
        );
      }
      const session = options.session;
      return (
        <BookingDetail
          hubName={hubName(session.hubId)}
          legs={legsOf(session.bookingId)}
          onBack={back}
          onOpenBooking={() => openBooking(session.bookingId)}
          onOpenLeg={openLeg}
          onShowOnMap={() => showOnMap(session.therapistId)}
          riderName={staffName}
          session={session}
          therapist={session.therapistId ? staffName(session.therapistId) : "Not assigned"}
        />
      );
    }
    if (selection.kind === "trip") {
      const rider = board.rows.find((row) =>
        row.blocks.some((block) => block.id === selection.legId),
      );
      const leg = rider?.blocks.find((block) => block.id === selection.legId);
      if (!rider || !leg || leg.kind === "session") return null;
      const session = board.sessions.find((entry) => entry.bookingId === leg.bookingId) ?? null;
      return (
        <TripDetail
          leg={leg}
          legs={legsOf(leg.bookingId)}
          onBack={back}
          onOpenLeg={openLeg}
          onOpenSession={() =>
            session ? setSelection({ kind: "booking", bookingId: session.bookingId }) : undefined
          }
          onShowOnMap={() => showOnMap(rider.staffId)}
          rider={rider}
          session={session}
          therapist={session?.therapistId ? staffName(session.therapistId) : "Not assigned"}
        />
      );
    }
    if (selection.kind === "person") {
      const row = board.rows.find((entry) => entry.staffId === selection.staffId);
      if (!row) return null;
      return (
        <PersonDetail
          editing={selection.editing}
          isToday={isToday(board)}
          nowMin={board.nowMin}
          onBack={back}
          onCancelEdit={() => setSelection({ ...selection, editing: false })}
          editLabel={actions ? undefined : "Edit in Schedules"}
          onEdit={() =>
            actions
              ? setSelection({ ...selection, editing: true })
              : navigate("/schedules", { state: here() })
          }
          onOpenBlock={selectBlock}
          onSaveShift={(shift) => saveShift(row.staffId, shift)}
          onShowOnMap={() => showOnMap(row.staffId)}
          row={row}
        />
      );
    }
    return (
      <HourDetailView
        detail={hourDetail(board, filters, selection.startMin)}
        hubName={hubName}
        onAssign={(bookingId) => setSelection({ kind: "assign", bookingId, pickedId: null })}
        onBack={back}
        onOpenPerson={(staffId) => setSelection({ kind: "person", staffId, editing: false })}
        scopeLabel={filters.hubId ? `${hubName(filters.hubId)} hub` : "All hubs"}
      />
    );
  }

  const selectedBookingId =
    selection?.kind === "booking" || selection?.kind === "assign" ? selection.bookingId : null;
  const selectedStaffId = selection?.kind === "person" ? selection.staffId : null;
  const selectedBlockId =
    selection?.kind === "trip"
      ? selection.legId
      : selectedBookingId
        ? `session:${selectedBookingId}`
        : null;

  return (
    <div className="relative flex h-[calc(100vh-7rem)] min-h-0 gap-3">
      <CalendarToolbar
        dateTitle={boardDateTitle(view, day, today, week)}
        filters={filters}
        hubs={source.hubs}
        onFiltersChange={changeFilters}
        onNext={() => step(1)}
        onPrevious={() => step(-1)}
        onToday={() => goTo(today)}
        onViewChange={(next) => {
          setView(next);
          setSelection(null);
        }}
        view={view}
      />

      <CalendarPanel
        detail={renderDetail()}
        fixes={fixes}
        hubName={hubName}
        onOpenFix={openFix}
        onOpenPerson={(staffId) => setSelection({ kind: "person", staffId, editing: false })}
        onOpenSession={(session) =>
          setSelection(
            session.phase === "unassigned"
              ? { kind: "assign", bookingId: session.bookingId, pickedId: null }
              : { kind: "booking", bookingId: session.bookingId },
          )
        }
        onTabChange={setTab}
        onTile={openTile}
        selectedBookingId={selectedBookingId}
        selectedStaffId={selectedStaffId}
        sessions={panelSessions}
        staffName={staffName}
        tab={tab}
        team={scoped.sections}
        tiles={tiles}
      />

      <section
        aria-label={view === "day" ? "Timeline" : "Week"}
        className="relative flex w-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-border bg-card shadow-card"
      >
        {view === "day" ? (
          <>
            <CalendarTimeline
              board={scoped}
              keyNote={
                simulated ? (
                  <Badge variant="warning">Simulated data — nothing here is real</Badge>
                ) : null
              }
              nowMin={board.nowMin}
              onSelectBlock={selectBlock}
              onSelectHour={(startMin) => setSelection({ kind: "hour", startMin })}
              onSelectPerson={(staffId) =>
                setSelection({ kind: "person", staffId, editing: false })
              }
              selectedBlockId={selectedBlockId}
              selectedHour={selection?.kind === "hour" ? selection.startMin : null}
              selectedStaffId={selectedStaffId}
              showNow={isToday(board)}
              strip={strip}
              zoom={zoom}
            />
            <div className="pointer-events-none absolute bottom-4 right-4 z-30">
              <CalendarControls
                isFullscreen={isFullscreen}
                onToggleFullscreen={() => void toggleFullscreen()}
                onZoomChange={setZoom}
                zoom={zoom}
              />
            </div>
          </>
        ) : weekModel ? (
          <>
            {simulated ? (
              <div className="flex justify-end border-b border-border/70 px-4 py-2.5">
                <Badge variant="warning">Simulated data — nothing here is real</Badge>
              </div>
            ) : null}
            <CalendarWeek
              onOpenDay={(next) => {
                goTo(next);
                setView("day");
              }}
              week={weekModel}
            />
          </>
        ) : null}
      </section>
    </div>
  );
}
